import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientContext = createContext(null);

// Resolve a logo_url to a displayable URL (handles file IDs, local paths, and full URLs)
async function resolveLogoUrl(logoUrl) {
  if (!logoUrl) return null;
  // Already a usable URL (local path or http)
  if (logoUrl.startsWith('/') || logoUrl.startsWith('http')) return logoUrl;
  // It's a file ID — fetch it as a blob
  try {
    const response = await fetch(`${API}/files/${logoUrl}`, {
      credentials: 'include',
      headers: { 'X-Client-Id': axios.defaults.headers.common['X-Client-Id'] || '' }
    });
    if (!response.ok) return null;
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export function ClientProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [clients, setClients] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevLogoRef = useRef(null);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data.clients);
      
      // Restore last selected client from localStorage, or use first
      const savedSlug = localStorage.getItem('activeClientSlug');
      const saved = data.clients.find(c => c.slug === savedSlug);
      if (saved) {
        setActiveClient(saved);
      } else if (data.clients.length > 0) {
        setActiveClient(data.clients[0]);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      fetchClients();
    } else {
      setLoading(false);
    }
  }, [user, authLoading, fetchClients]);

  // Set axios default header, CSS variables, and resolve logo whenever activeClient changes
  useEffect(() => {
    if (activeClient) {
      axios.defaults.headers.common['X-Client-Id'] = activeClient.id;
      localStorage.setItem('activeClientSlug', activeClient.slug);
      // Apply dynamic branding via CSS custom properties
      const root = document.documentElement;
      root.style.setProperty('--brand-primary', activeClient.primary_color || '#1F467C');
      root.style.setProperty('--brand-secondary', activeClient.secondary_color || '#5CB85C');
      root.style.setProperty('--brand-accent', activeClient.accent_color || '#3980C3');

      // Resolve logo URL (revoke previous blob URL to prevent memory leak)
      if (prevLogoRef.current && prevLogoRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(prevLogoRef.current);
      }
      resolveLogoUrl(activeClient.logo_url).then(url => {
        setResolvedLogoUrl(url);
        prevLogoRef.current = url;
      });
    }
  }, [activeClient]);

  const switchClient = useCallback((clientIdOrSlug) => {
    const client = clients.find(c => c.id === clientIdOrSlug || c.slug === clientIdOrSlug);
    if (client) {
      // Update localStorage IMMEDIATELY so the axios interceptor picks up the right client
      localStorage.setItem('activeClientSlug', client.slug);
      setActiveClient(client);
    }
  }, [clients]);

  const addClient = useCallback(async (clientData) => {
    const { data } = await axios.post(`${API}/clients`, clientData);
    setClients(prev => [...prev, data]);
    localStorage.setItem('activeClientSlug', data.slug);
    setActiveClient(data);
    return data;
  }, []);

  const refreshClients = useCallback(async () => {
    const { data } = await axios.get(`${API}/clients`);
    setClients(data.clients);
    // Update active client in case it was modified
    if (activeClient) {
      const updated = data.clients.find(c => c.id === activeClient.id);
      if (updated) setActiveClient(updated);
    }
  }, [activeClient]);

  return (
    <ClientContext.Provider value={{
      clients,
      activeClient,
      resolvedLogoUrl,
      loading,
      switchClient,
      addClient,
      refreshClients,
    }}>
      {children}
    </ClientContext.Provider>
  );
}

export function useClient() {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error('useClient must be used within ClientProvider');
  return ctx;
}
